import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { Article20CopyLink } from "scmp-app/components/article/article-2.0-action-widget/copy-link";
import { Article20Comment } from "scmp-app/components/article/article-comments-trigger/article-2.0";
import { EntityLink } from "scmp-app/components/entity-link";
import { commentHash, useArticleCommentLink } from "scmp-app/lib/hooks";
import type { actionWidgetContent$key } from "scmp-app/queries/__generated__/actionWidgetContent.graphql";

import {
  Container,
  StyledArticlePrint,
  StyledCommentsTrigger,
  StyledContentBookmark,
} from "./styles";
import type {
  BookmarkOption,
  CommentOption,
  CopyLinkOptions,
  Options,
  PrintOption,
  Variant,
} from "./types";

const optionsMap: Record<Variant, Options> = {
  "article-2.0": {
    container: { $orientation: "row", $variant: "article-2.0" },
    items: [
      { props: { position: "article_header", variant: "article-2.0" }, type: "bookmark" },
      { props: { variant: "article-2.0" }, type: "comment" },
      { props: {}, type: "print" },
    ],
  },
  "article-2.0-before-you-go": {
    container: { $orientation: "row", $variant: "article-2.0" },
    items: [{ props: { position: "article_bottom", variant: "article-2.0" }, type: "bookmark" }],
  },
  default: {
    container: { $orientation: "column" },
    items: [
      { props: { variant: "widgetLight" }, type: "comment" },
      { props: { $variant: "light" }, type: "bookmark" },
    ],
  },
  "magazines-style": {
    container: { $orientation: "row", $variant: "magazines-style" },
    items: [
      { props: { variant: "button" }, type: "comment" },
      { props: { $size: "small", $variant: "light" }, type: "bookmark" },
    ],
  },
  photoEssay: {
    container: { $orientation: "column" },
    items: [
      { props: { variant: "widgetDark" }, type: "comment" },
      { props: { $variant: "dark" }, type: "bookmark" },
    ],
  },
  series: {
    container: { $orientation: "row", $variant: "article-2.0" },
    items: [
      { props: {}, type: "copy-link" },
      { props: { variant: "article-2.0" }, type: "bookmark" },
    ],
  },
  video: {
    container: { $orientation: "row", $variant: "video" },
    items: [{ props: { position: "article_header", variant: "article-2.0" }, type: "bookmark" }],
  },
};

type Props = {
  className?: string;
  contentCommentLinkMode?: "navigate" | "toggle";
  reference: actionWidgetContent$key;
  variant?: Variant;
};

export const ActionWidget: FunctionComponent<Props> = ({
  className,
  contentCommentLinkMode = "toggle",
  reference,
  variant = "default",
}) => {
  const content = useFragment(
    graphql`
      fragment actionWidgetContent on Content {
        __typename
        entityId
        ... on Article {
          ...articleCommentsTriggerArticle
          ...articlePrintArticle
        }
        ...copyLinkArticle20CopyLinkContent
        ...entityLink
      }
    `,
    reference,
  );

  const isArticle = content.__typename === "Article";
  const { toggleByUrl: toggleCommentByUrl } = useArticleCommentLink();

  const handleRenderContentComment = () => {
    if (contentCommentLinkMode === "navigate") {
      return (
        <EntityLink hash={commentHash} key="content-comment" reference={content}>
          <Article20Comment />
        </EntityLink>
      );
    }
    return <Article20Comment key="content-comment" onClick={toggleCommentByUrl} />;
  };

  const handleRenderCommentsTrigger = ({ props }: CommentOption) => (
    <StyledCommentsTrigger
      key="comment-trigger"
      reference={content}
      variant="widgetLight"
      {...props}
    />
  );

  const handleRenderContentBookmark = ({ props }: BookmarkOption) => (
    <StyledContentBookmark
      entityId={content.entityId}
      ga4Events={{
        click: {
          action: "click",
          category: "bookmark",
          customized_parameters: {
            action_type: "add",
            article_id: content.entityId,
            trigger_point: props.position ?? "article_side",
          },
          subcategory: "",
        },
      }}
      key="content-bookmark"
      location="side"
      {...props}
    />
  );

  const handleRenderPrint = ({ props }: PrintOption) => (
    <StyledArticlePrint $variant={variant} key="content-print" {...props} reference={content} />
  );

  const handleRenderCopyLink = ({ props }: CopyLinkOptions) => (
    <Article20CopyLink key="copy-link" {...props} reference={content} />
  );

  const { container, items } = optionsMap[variant];

  return (
    <Container {...container} className={className}>
      {items.map(item => {
        switch (item.type) {
          case "bookmark":
            return handleRenderContentBookmark(item);
          case "comment":
            if (!isArticle) return null;
            return handleRenderCommentsTrigger(item);
          case "content-comment":
            return handleRenderContentComment();
          case "copy-link":
            return handleRenderCopyLink(item);
          case "print":
            return handleRenderPrint(item);
        }
      })}
    </Container>
  );
};
ActionWidget.displayName = "ActionWidget";
